import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  AlertAppearances,
  AlertOrientations,
  Button,
  ButtonVariants,
  ButtonSizes,
  BadgeVariants,
} from '@carboledger/ui';
import { useTranslations } from '@/hooks';
import { ROUTES } from '@/app/routes';
import { LoadingOverlay, EmptyState } from '@/components/feedback';
import { DetailPageHeader } from '@/components/common';
import { ALLOCATION_STATUS_TONE } from '@/config/statusTones';
import { EMPTY_LIST } from '@/lib/utils';
import {
  useGetAllocationDetailQuery,
  useGetAllocationDeclarationQuery,
} from '../api/allocationsApi';
import { ALLOCATION_ACTION_VISIBILITY } from '../constants/allocationActions';
import { allocationBanner } from '../constants/allocationBanner';
import { ALLOCATION_CONFIRM } from '../constants/allocationConfirm';
import { AllocationConfirmDialog } from '../components/AllocationConfirmDialog';
import { AllocationOverviewTabView } from '../components/AllocationOverviewTabView';
import { AllocationAttachmentsTabView } from '../components/AllocationAttachmentsTabView';
import { UpdateShipmentDetailsModal } from '../components/UpdateShipmentDetailsModal';
import { GenerateDeclarationModal } from '../components/GenerateDeclarationModal';
import { buildEditDeclarationValues } from '../constants/editDeclarationValues';
import {
  useDeclarationPdf,
  useSaveDeclaration,
  useDownloadDeclaration,
  useUploadDeclarationFile,
  saveRenderedDeclaration,
  useSaveShipmentDetails,
} from '../hooks';
import { LinkShipmentsForAllocation } from '../components/LinkShipmentsForAllocation';
import styles from './AllocationDetailPage.module.scss';

const DetailTab = Object.freeze({
  overview: 'overview',
  attachments: 'attachments',
});

// Outbounds ▸ Allocations ▸ Allocation details.
export const AllocationDetailPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module, allocationId } = useParams();
  const [activeTab, setActiveTab] = useState(DetailTab.overview);
  const [isUpdateShipmentOpen, setIsUpdateShipmentOpen] = useState(false);
  const [isDeclarationOpen, setIsDeclarationOpen] = useState(false);
  const [isLinkOpen, setIsLinkOpen] = useState(false);
  // Edit, reject and revoke each ask first.
  const [confirm, setConfirm] = useState(null);

  const {
    data: allocation,
    isLoading,
    isError,
  } = useGetAllocationDetailQuery({ module, id: allocationId });

  const goToAllocations = useCallback(
    () => navigate(ROUTES.allocations(module)),
    [navigate, module],
  );

  const handleUpdateShipmentDetails = useCallback(() => setIsUpdateShipmentOpen(true), []);
  const handleCloseUpdateShipment = useCallback(() => setIsUpdateShipmentOpen(false), []);

  const { save: saveShipmentDetails, isSaving: isSavingShipments } = useSaveShipmentDetails(module);
  const handleSaveShipmentDetails = useCallback(
    async (shipments) => {
      if (await saveShipmentDetails(allocation, shipments)) setIsUpdateShipmentOpen(false);
    },
    [saveShipmentDetails, allocation],
  );

  const handleGenerateDeclaration = useCallback(() => setIsDeclarationOpen(true), []);
  const handleCloseDeclaration = useCallback(() => setIsDeclarationOpen(false), []);

  const handleLinkExistingShipment = useCallback(() => setIsLinkOpen(true), []);
  const handleCloseLink = useCallback(() => setIsLinkOpen(false), []);
  const goToEditDeclaration = useCallback(
    () => navigate(ROUTES.allocationEditDeclaration(module, allocationId)),
    [navigate, module, allocationId],
  );
  // Editing is a one-shot, so both entry points warn before they navigate.
  const handleEditDeclaration = useCallback(
    () => setConfirm({ kind: ALLOCATION_CONFIRM.edit }),
    [],
  );

  // TODO: no route links an outgoing shipment to an allocation yet — wire this up once it ships.
  const handleSaveLink = useCallback(() => {}, []);
  const handleLinkSearchChange = useCallback(() => {}, []);

  const handleRevoke = useCallback(() => setConfirm({ kind: ALLOCATION_CONFIRM.revoke }), []);
  const handleMarkRejected = useCallback(() => setConfirm({ kind: ALLOCATION_CONFIRM.reject }), []);
  const handleCloseConfirm = useCallback(() => setConfirm(null), []);
  const confirmWithRow = useMemo(
    () => confirm && { kind: confirm.kind, row: allocation },
    [confirm, allocation],
  );

  // The PoS fields live on the declaration route, not on the fulfillment DTO.
  const { data: declaration, isLoading: isDeclarationLoading } = useGetAllocationDeclarationQuery({
    module,
    id: allocationId,
  });

  const declarationValues = useMemo(
    () => buildEditDeclarationValues(allocation, declaration),
    [allocation, declaration],
  );
  // Opening the preview only reads and renders; both writes wait for Confirm.
  const { previewBlob } = useDeclarationPdf({
    storedFileId: declaration?.declarationFileId,
    module,
    allocationId,
    values: declarationValues,
    // Rendering before the first read lands would upload a PDF missing every PoS field.
    enabled: isDeclarationOpen && !isDeclarationLoading,
  });
  // Nothing is generated yet here, so Confirm is the first save: a POST.
  const { save } = useSaveDeclaration({ module, allocationId });
  const uploadDeclarationFile = useUploadDeclarationFile(module);
  const downloadDeclaration = useDownloadDeclaration(module);
  const handleDownloadDeclaration = useCallback(
    () => downloadDeclaration(allocationId, declarationValues, previewBlob),
    [downloadDeclaration, allocationId, declarationValues, previewBlob],
  );

  // "Confirm declaration & Download": upload the document, save it, then hand it over.
  const handleConfirmDeclaration = useCallback(async () => {
    const uploadedFileId = await uploadDeclarationFile(allocationId, previewBlob);
    const result = await save({ ...declarationValues, siteId: allocation?.siteId }, uploadedFileId);
    if (result.error) return;
    // Close first: the download is the last step and nothing on screen waits for it.
    setIsDeclarationOpen(false);
    saveRenderedDeclaration(previewBlob, declarationValues);
  }, [
    uploadDeclarationFile,
    allocationId,
    previewBlob,
    save,
    declarationValues,
    allocation?.siteId,
  ]);

  const tabs = useMemo(
    () => [
      { value: DetailTab.overview, label: t('SECTION_OVERVIEW') },
      { value: DetailTab.attachments, label: t('SECTION_ATTACHMENTS') },
    ],
    [t],
  );

  const breadcrumbItems = useMemo(
    () => [
      { label: t('ALLOCATIONS_TITLE'), onClick: goToAllocations },
      { label: allocation?.transactionId ?? '-' },
    ],
    [t, goToAllocations, allocation?.transactionId],
  );

  const metaFields = useMemo(() => {
    if (!allocation) return [];
    return [
      { id: 'material', label: t('FIELD_MATERIAL_NAME'), value: allocation.materialName },
      {
        id: 'status',
        label: t('FIELD_STATUS'),
        value: allocation.status,
        type: 'status',
        tone: ALLOCATION_STATUS_TONE[allocation.statusCode] ?? BadgeVariants.default,
      },
      { id: 'sentFrom', label: t('ALLOCATIONS_FIELD_SENT_FROM'), value: allocation.facilityName },
      { id: 'terminal', label: t('FIELD_TERMINAL'), value: allocation.terminal },
      { id: 'vessel', label: t('FIELD_VESSEL_NAME'), value: allocation.vesselName },
      { id: 'soldTo', label: t('FIELD_SOLD_TO'), value: allocation.sendTo },
      { id: 'shippingTo', label: t('FIELD_SHIPPING_TO'), value: allocation.shippingTo },
      {
        id: 'transactionDate',
        label: t('ALLOCATIONS_COL_ALLOCATION_DATE'),
        value: allocation.transactionDate,
      },
    ];
  }, [t, allocation]);

  // The header shows exactly the CTAs this status allows; the rest of the set stays hidden.
  const headerActions = useMemo(() => {
    if (!allocation) return null;
    const buttons = [
      {
        id: 'generate-declaration',
        label: t('ALLOCATIONS_ACTION_GENERATE_DECLARATION'),
        isVisible: ALLOCATION_ACTION_VISIBILITY.generateDeclaration,
        onClick: handleGenerateDeclaration,
      },
      {
        id: 'download-declaration',
        label: t('ALLOCATIONS_ACTION_DOWNLOAD_DECLARATION'),
        isVisible: ALLOCATION_ACTION_VISIBILITY.downloadDeclaration,
        onClick: handleDownloadDeclaration,
      },
      {
        id: 'edit-declaration',
        label: t('ALLOCATIONS_ACTION_EDIT_DECLARATION'),
        isVisible: ALLOCATION_ACTION_VISIBILITY.editDeclaration,
        onClick: handleEditDeclaration,
      },
      {
        id: 'update-shipment-details',
        label: t('ALLOCATIONS_ACTION_UPDATE_SHIPMENT_DETAILS'),
        isVisible: ALLOCATION_ACTION_VISIBILITY.updateShipmentDetails,
        onClick: handleUpdateShipmentDetails,
      },
      {
        id: 'link-existing-shipment',
        label: t('ALLOCATIONS_ACTION_LINK_EXISTING_SHIPMENT'),
        isVisible: ALLOCATION_ACTION_VISIBILITY.linkExistingShipment,
        onClick: handleLinkExistingShipment,
      },
      {
        id: 'mark-rejected',
        label: t('ALLOCATIONS_ACTION_MARK_REJECTED'),
        isVisible: ALLOCATION_ACTION_VISIBILITY.markRejected,
        onClick: handleMarkRejected,
      },
      {
        id: 'revoke',
        label: t('ALLOCATIONS_ACTION_REVOKE'),
        isVisible: ALLOCATION_ACTION_VISIBILITY.revoke,
        onClick: handleRevoke,
      },
    ].filter((button) => button.isVisible(allocation));

    return buttons.map((button) => (
      <Button
        key={button.id}
        variant={ButtonVariants.secondary}
        size={ButtonSizes.sm}
        onClick={button.onClick}
      >
        {button.label}
      </Button>
    ));
  }, [
    t,
    allocation,
    handleGenerateDeclaration,
    handleDownloadDeclaration,
    handleEditDeclaration,
    handleUpdateShipmentDetails,
    handleLinkExistingShipment,
    handleMarkRejected,
    handleRevoke,
  ]);

  const banner = allocationBanner(t, allocation?.statusCode);
  // The preview modal takes a list because the list screen opens it for a bulk selection too.
  const declarationRows = useMemo(() => (allocation ? [allocation] : EMPTY_LIST), [allocation]);
  const canEditDeclaration = useMemo(
    () => Boolean(allocation) && ALLOCATION_ACTION_VISIBILITY.editFromPreview(allocation),
    [allocation],
  );
  // Only the one edit after generating warns first; before that it is just filling the form in.
  const handleEditFromPreview = useCallback(() => {
    if (ALLOCATION_ACTION_VISIBILITY.editDeclaration(allocation)) handleEditDeclaration();
    else goToEditDeclaration();
  }, [allocation, handleEditDeclaration, goToEditDeclaration]);

  if (isLoading) {
    return <LoadingOverlay label={t('COMMON_LOADING')} />;
  }

  if (isError || !allocation) {
    return (
      <EmptyState
        title={t('ALLOCATIONS_DETAIL_LOAD_ERROR')}
        action={
          <Button variant={ButtonVariants.secondary} onClick={goToAllocations}>
            {t('COMMON_BACK')}
          </Button>
        }
      />
    );
  }

  return (
    <div className={styles.page}>
      <DetailPageHeader
        breadcrumbItems={breadcrumbItems}
        title={allocation.transactionId}
        actions={headerActions}
        metaFields={metaFields}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabsAriaLabel={t('ALLOCATIONS_TITLE')}
      />

      <div className={styles.content}>
        {banner && (
          <Alert
            variant={banner.variant}
            appearance={AlertAppearances.accent}
            orientation={AlertOrientations.horizontal}
          >
            {banner.message}
          </Alert>
        )}

        {activeTab === DetailTab.overview && <AllocationOverviewTabView allocation={allocation} />}

        {activeTab === DetailTab.attachments && (
          <AllocationAttachmentsTabView
            module={module}
            allocationId={allocationId}
            transactionId={allocation.transactionId}
          />
        )}
      </div>

      <UpdateShipmentDetailsModal
        open={isUpdateShipmentOpen}
        onClose={handleCloseUpdateShipment}
        allocation={allocation}
        isSaving={isSavingShipments}
        onSubmit={handleSaveShipmentDetails}
      />

      <GenerateDeclarationModal
        open={isDeclarationOpen}
        onClose={handleCloseDeclaration}
        allocations={declarationRows}
        file={previewBlob}
        canEdit={canEditDeclaration}
        onEdit={handleEditFromPreview}
        onComplete={goToEditDeclaration}
        onDownload={handleDownloadDeclaration}
        onConfirm={handleConfirmDeclaration}
      />

      <AllocationConfirmDialog
        module={module}
        confirm={confirmWithRow}
        onClose={handleCloseConfirm}
        onEdit={goToEditDeclaration}
      />

      {/* TODO: the candidate list needs the allocation's declaration batch id on the DTO. */}
      <LinkShipmentsForAllocation
        open={isLinkOpen}
        onClose={handleCloseLink}
        allocation={allocation}
        onSearchChange={handleLinkSearchChange}
        onSave={handleSaveLink}
      />
    </div>
  );
};
